import { ChildProps } from '@apollo/client/react/hoc';
import { gql } from '@apollo/client';
import React from 'react';
import { get, isEqual } from 'lodash';

import { withGraphql } from '../../with_graphql';
import SanitizedRender from '../../components/sanitized_render';
import ListingsCollection, { ListingsCollection as ListingsCollectionClass } from '../../components/listings_collection';

import {
  CommonsCmsRecentlyViewedListings,
  core_apimessages_Ribbon_Reason as IRibbonReason,
} from '../../rql';

import { formatExperimentsForEventAttributes, userContextIsTrackable } from '../../elog/mparticle';
import { MParticleEventName, trackEvent } from '../../elog/mparticle_tracker';

import { IDynamicComponentProps } from '../dynamic_component_props';
import { IUserContext, withUserContext } from '../../components/user_context_provider';
import { isExperimentEnabled } from '../../user_context_helpers';
import experiments from '../../experiments';
import { mParticleListingFields } from '../../components/with_mparticle_listings';
import { OverflowingRowAction } from '../../components/overflowing_row_action';
import RecentlyViewedAndMoreListings from '../../components/recently_viewed_and_more_listings';

interface ExternalProps extends IDynamicComponentProps {
  onSale?: boolean;
  ctaText?: string;
  ctaTargetHref?: string;
  largeTiles?: boolean;
  title?: string;
  titleHtml?: string;
  displayAsGrid?: boolean;
  jumplinkSlug?: string;
  loggedOutExperiment?: boolean;
  onVisible?: () => void;
  displayAddToCart?: boolean;
  trackName?: string;
  experimentName?: string;
  showFlags?: boolean;
  showShippingDisplay?: boolean;
  newHomepageDesign?: boolean;
}

export type IProps = ExternalProps & IUserContext;

const COMPONENT_NAME = 'RecentlyViewedListings';
const DEFAULT_COUNT = 12;
const MIN_COUNT = 4;
const EXPERIMENTAL_MAX_COUNT = 6;
const EXPERIMENTAL_MIN_COUNT = 6;

export class RecentlyViewedListings extends React.Component<
ChildProps<IProps, CommonsCmsRecentlyViewedListings.Query, CommonsCmsRecentlyViewedListings.Variables>,
null
> {

  static defaultProps: Partial<IProps> = {
    loggedOutExperiment: false,
    newHomepageDesign: false,
  };

  componentDidMount() {
    this.logView();

    const { onVisible } = this.props;
    if (this.showListings() && onVisible) {
      onVisible();
    }
  }

  componentDidUpdate(oldProps) {
    const newListings = get(this.props, 'data.recentlyViewedListings', []);
    const oldListings = get(oldProps, 'data.recentlyViewedListings', []);
    if (!isEqual(newListings, oldListings)) {
      this.logView();
    }

    const { onVisible } = this.props;
    if (!isEqual(newListings, oldListings) && this.showListings() && onVisible) {
      onVisible();
    }
  }

  logView() {
    if (!this.hasEnoughListings()) return;

    const experimentTracking = !this.props.experimentName ? {} : {
      experiments: formatExperimentsForEventAttributes(this.props.user, [this.props.experimentName]),
    };

    const viewData = {
      componentName: this.props.trackName || COMPONENT_NAME,
      eventName: MParticleEventName.ComponentView,
      ...experimentTracking,
    };

    trackEvent(viewData);
  }

  minListingCount = () => this.props.newHomepageDesign ? EXPERIMENTAL_MIN_COUNT : MIN_COUNT;

  maxListingCount = () => this.props.newHomepageDesign ? EXPERIMENTAL_MAX_COUNT : DEFAULT_COUNT;

  hasEnoughListings() {
    return !this.loading() && this.listings().length >= this.minListingCount();
  }

  renderCTA() {
    if (!this.props.ctaTargetHref) {
      return null;
    }

    return (
      <OverflowingRowAction
        href={this.props.ctaTargetHref}
        text={this.props.ctaText}
      />
    );
  }

  saleListings() {
    const listings = get(this.props, 'data.recentlyViewedListings');

    const salesListings = listings.filter((listing) => {
      const reason = get(listing, 'pricing.ribbon.reason', '');
      return reason === IRibbonReason.SALE;
    });

    return salesListings.slice(0, this.maxListingCount() - 1);
  }

  loading() {
    return get(this.props, 'data.loading');
  }

  listings(): CommonsCmsRecentlyViewedListings.RecentlyViewedListings[] {
    const listings = get(this.props, 'data.recentlyViewedListings');

    if (this.props.onSale && listings) {
      return this.saleListings();
    }

    return (listings || []).slice(0, this.maxListingCount());
  }

  renderTitle() {
    if (!this.props.title) return null;

    return (
      <SanitizedRender
        html={this.props.titleHtml || this.props.title}
      />
    );
  }

  showListings() {
    return this.hasEnoughListings();
  }

  render() {
    if (!this.showListings()) {
      if (this.props.newHomepageDesign && !this.loading() && this.listings().length) {
        return (
          <RecentlyViewedAndMoreListings
            recentlyViewedListings={this.listings()}
            largeTiles={this.props.largeTiles}
            maxCount={this.maxListingCount()}
            displayAsGrid={this.props.displayAsGrid}
            jumplinkSlug={this.props.jumplinkSlug}
            displayAddToCart={this.props.displayAddToCart}
            showFlags={this.props.showFlags}
            showShippingDisplay={this.props.showShippingDisplay}
          />
        );
      } else {
        return null;
      }
    }

    if (this.props.experimentName && !isExperimentEnabled(this.props.user, this.props.experimentName)) {
      return null;
    }

    return (
      <ListingsCollection
        callToAction={this.renderCTA()}
        componentName={this.props.trackName || COMPONENT_NAME}
        displayAsGrid={this.props.displayAsGrid}
        largeTiles={this.props.largeTiles}
        listings={this.listings()}
        loading={this.loading()}
        maxCount={this.maxListingCount()}
        title={this.renderTitle()}
        jumplinkSlug={this.props.jumplinkSlug}
        displayAddToCart={this.props.displayAddToCart}
        showFlags={this.props.showFlags}
        showShippingDisplay={this.props.showShippingDisplay}
      />
    );
  }
}

export const RECENTLY_VIEWED_LISTINGS_QUERY = gql`
  query Commons_Cms_RecentlyViewedListings(
    $limit: Int
    $showListingLocation: Boolean!
    $shouldSkipTracking: Boolean!
  ) {
    recentlyViewedListings(input: {
      limit: $limit
    }) {
      _id
      ... ListingsCollection
      ... mParticleListingFields @skip(if: $shouldSkipTracking)
      ... ShopFields @include(if: $showListingLocation)
    }
  }
  ${ListingsCollectionClass.fragments.listing}
  ${mParticleListingFields}
  ${ListingsCollectionClass.fragments.shopFields}
`;

const connect = withGraphql<IProps, CommonsCmsRecentlyViewedListings.Query, CommonsCmsRecentlyViewedListings.Variables>(
  RECENTLY_VIEWED_LISTINGS_QUERY,
  {
    options: ownProps => ({
      ssr: ownProps.ssr,
      variables: {
        limit: ownProps.onSale ? 20 : DEFAULT_COUNT,
        showListingLocation: isExperimentEnabled(ownProps.user, experiments.LISTING_CARD_LOCATION),
        shouldSkipTracking: !userContextIsTrackable(ownProps.user),
      },
    }),
  },
);

export default withUserContext<IProps>(connect(RecentlyViewedListings));
